import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { SiteFooter } from './SiteFooter';
import { SiteHeader } from './SiteHeader';
import { scrollTargetFor, type Place } from './siteNav';

export function MarketingLayout() {
  const { pathname, search, hash, key } = useLocation();
  const lastPlace = useRef<Place | null>(null);

  // Scroll after each navigation (TOM-980): to the element a hash names, so
  // /#neo and /#how work from other pages and on "/"; otherwise, or when no
  // element has that id, to the top. `key` is in the deps so a second click on
  // the same link scrolls again. The scroll waits a frame so the page has laid
  // out.
  useEffect(() => {
    const target = scrollTargetFor(lastPlace.current, { pathname, search, hash });
    lastPlace.current = { pathname, search, hash };
    if (target.kind === 'none') return;
    const frame = window.requestAnimationFrame(() => {
      const el = target.kind === 'element' ? document.getElementById(target.id) : null;
      if (el) el.scrollIntoView();
      else window.scrollTo(0, 0);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname, search, hash, key]);

  useEffect(() => {
    const reveals = document.querySelectorAll<HTMLElement>('.reveal');
    if (reveals.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [pathname]);

  return (
    <>
      <SiteHeader />
      <Outlet />
      <SiteFooter />
    </>
  );
}
